import classNames from "classnames";
import { FormattedTextString, Key } from "client/Misc/commonTypes";
import {
	delay,
	resolveFormattedTextString,
	setTrackingId
} from "client/Misc/utils";
import {
	ChangeEvent,
	ReactElement,
	useContext,
	FocusEvent,
	forwardRef,
	Ref,
	useImperativeHandle,
	useRef,
	RefObject,
	useCallback,
	KeyboardEvent,
	SyntheticEvent
} from "react";
import { UniqueInputIdContext } from "../../Contexts/UniqueInputIdContext";
import styles from "./TextArea.module.less";
import { KeyEventArguments } from "../TextInput/TextInput";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";

export type TextAreaHandle = {
	focus: () => void;
	insert: (text: string) => void;
};

type VerticalOverflowKind = "auto" | "hidden";

export type TextAreaProps = {
	value: string | null;
	onChange(value:string): void;
	onBlur?(value: string): void;
	onFocus?(value: string): void;
	onKeyDown?: (args: KeyEventArguments) => boolean;
	trackingId?: string;
	placeholder?: string | FormattedTextString;
	isDisabled?: boolean;
	isInvalid?: boolean;
	autoFocus?: boolean;
	isBorderless?: boolean;
	hasNoPadding?: boolean;
	hasNoBackgroundColor?: boolean;
	tabIndex?: number;
	verticalOverflow?: VerticalOverflowKind;
	onSelectionChange?: (from: number, to: number) => void;
} & ({
	height: "fill";
	isResizable?: undefined;
	rows?: undefined;
} | {
	height?: "static";
	isResizable?: boolean;
	rows?: number;
});

export const TextArea = forwardRef((props: TextAreaProps, ref: Ref<TextAreaHandle>): ReactElement => {
	const {
		value,
		onChange,
		onBlur,
		onFocus,
		onKeyDown,
		isDisabled = false,
		isInvalid = false,
		isResizable = false,
		placeholder,
		rows,
		trackingId,
		autoFocus,
		height = "static",
		isBorderless = false,
		hasNoPadding = false,
		hasNoBackgroundColor = false,
		tabIndex,
		verticalOverflow = "auto",
		onSelectionChange
	} = props;

	const { id } = useContext(UniqueInputIdContext);
	const elementRef: RefObject<HTMLTextAreaElement | null> = useRef<HTMLTextAreaElement>(null);

	const onValueChange = (args: ChangeEvent<HTMLTextAreaElement>): void => {
		const newValue: string = args.target.value;
		onChange(newValue);
	};

	const onBlurInvocator = (args: FocusEvent<HTMLTextAreaElement>): void => {
		if (!onBlur) {
			return;
		}

		const newValue: string = args.target.value;
		onBlur(newValue);
	};

	const focusHandler = (args: FocusEvent<HTMLTextAreaElement>): void => {
		if (!onFocus) {
			return;
		}
		const newValue: string = args.target.value;
		onFocus(newValue);
	};

	const keydownHandler = useCallback(
		(event: KeyboardEvent<HTMLTextAreaElement>): void => {
			if (!onKeyDown) {
				return;
			}

			const key: Key | null = tryResolveKeyboardKey(event);

			const result: boolean = onKeyDown({
				key,
				modifiers: {
					shift: event.shiftKey,
					capsLock: event.getModifierState("CapsLock")
				}
			});

			if (result) {
				event.preventDefault();
			}
		},
		[onKeyDown]
	);

	const selectHandler = (args: SyntheticEvent<HTMLTextAreaElement, Event>): void => {
		if(!onSelectionChange) {
			return;
		}

		const target: EventTarget = args.target;

		if (!(target instanceof HTMLTextAreaElement)) {
			return;
		}

		onSelectionChange(target.selectionStart, target.selectionEnd);
	};

	useImperativeHandle(
		ref,
		() => ({
			focus: (): void => {
				elementRef.current?.focus();
			},
			insert: async (text: string): Promise<void> => {
				const textAreaElement: HTMLTextAreaElement | null = elementRef.current;
				if (textAreaElement === null) {
					return;
				}

				const currentValue: string = textAreaElement.value;
				const selectionStart: number = textAreaElement.selectionStart;
				const selectionEnd: number = textAreaElement.selectionEnd;
				const updatedValue: string = `${currentValue.slice(0, selectionStart)}${text}${currentValue.slice(selectionEnd)}`;
				const nextCursorPosition: number = selectionStart + text.length;

				onChange(updatedValue);

				// Wait for the value to be updated before focusing the text area
				await delay(1);
				textAreaElement.focus();
				textAreaElement.setSelectionRange(nextCursorPosition, nextCursorPosition);
			}
		}),
		[onChange]
	);

	return (
		<textarea
			{...setTrackingId(trackingId)}
			id={id}
			className={classNames(
				styles.root,
				verticalOverflowStyleMap[verticalOverflow],
				{
					[styles.root_isInvalid]: isInvalid,
					[styles.root_noResize]: !isResizable,
					[styles.root_fillHeight]: height === "fill",
					[styles.root_borderless]: isBorderless,
					[styles.root_hasNoPadding]: hasNoPadding,
					[styles.root_hasNoBackgroundColor]: hasNoBackgroundColor
				}
			)}
			value={value ?? ""}
			onChange={onValueChange}
			onBlur={onBlurInvocator}
			onFocus={focusHandler}
			onKeyDown={keydownHandler}
			placeholder={resolveFormattedTextString(placeholder)}
			rows={rows}
			disabled={isDisabled}
			autoFocus={autoFocus}
			ref={elementRef}
			tabIndex={tabIndex}
			onSelect={selectHandler}
		/>
	);
});
TextArea.displayName = "TextArea";

const verticalOverflowStyleMap: Record<VerticalOverflowKind, string | undefined> = {
	["auto"]: undefined, // It is implicit
	["hidden"]: styles.root_noOverflow
};

export type TextAreaReadOnlyProps = {
	value: string | null;
	trackingId?: string;
} & ({
	height: "fill";
	rows?: undefined;
} | {
	height?: "static";
	rows?: number;
});

export const TextAreaReadOnly = (props: TextAreaReadOnlyProps): ReactElement => {
	const {
		value,
		height,
		rows,
		trackingId
	} = props;

	return (
		<textarea
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				styles.root_noResize,
				styles.root_hasNoBackgroundColor,
				styles.root_borderless,
				{
					[styles.root_fillHeight]: height === "fill"
				}
			)}
			value={value ?? ""}
			rows={rows}
			readOnly
		/>
	);
};
TextAreaReadOnly.displayName = "TextAreaReadOnly";
